import * as Base from "@base-ui/react"
import type { QueryResultType } from "@rocicorp/zero"
import { useQuery, useZeroOnline } from "@rocicorp/zero/react"
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from "@tanstack/react-router"
import * as React from "react"
import * as z from "zod/mini"
import type { Row } from "../../generated/schema.ts"
import * as Nanoid from "../../shared/nanoid.ts"
import * as queries from "../../shared/queries.ts"
import { Button } from "../components/Button.tsx"
import { Message } from "../components/Message.tsx"
import { MessageEditor } from "../components/MessageEditor.tsx"
import { ReactionPicker } from "../components/ReactionPicker.tsx"
import { Spinner } from "../components/Spinner.tsx"
import { useHasChannelPermission } from "../hooks/useHasChannelPermission.ts"
import { useVirtualizer } from "../hooks/useVirtualizer.ts"
import * as App from "../lib/app.ts"
import * as Auth from "../lib/auth.ts"
import * as Zero from "../lib/zero.ts"
import { Layout } from "./-components/Layout.tsx"

export const Route = createFileRoute("/$owner/$repo/~/$channel")({
  async beforeLoad(opts) {
    const { context, params } = opts
    const channel =
      "channel" in params && typeof params.channel === "string"
        ? context.repository.channels.find(
            (channel) => channel.name === params.channel,
          )
        : undefined
    if (!channel) throw redirect({ to: "/$owner/$repo", params })
    return { channel, channelId: channel.id, repository: context.repository }
  },
  component: RouteComponent,
  head(ctx) {
    const { channel, owner, repo } = ctx.params
    return {
      meta: [{ title: `#${channel} - ${owner}/${repo}` }],
    }
  },
  async loader(opts) {
    const { context } = opts
    context.zero.preload(
      queries.messagesForChannel(context.authData, {
        channelId: context.channel.id,
        direction: "backward",
        limit: cursorSize,
      }),
    )
    context.zero.preload(
      queries.channelWithPermissions(context.authData, {
        id: context.channel.id,
      }),
    )
    if (context.authData)
      context.zero.preload(
        queries.accountWithMemberships(context.authData, {
          channelId: context.channel.id,
          organizationId:
            context.repository.owner.type === "organization"
              ? context.repository.owner.id
              : "none",
          repositoryId: context.repository.id,
        }),
      )
  },
  validateSearch: z.object({
    max: z.optional(z.boolean()),
    messageId: z.optional(z.union([z.string(), z.undefined()])),
    threadId: z.optional(z.union([z.string(), z.undefined()])),
    start: z.optional(z.union([z.string(), z.undefined()])),
  }),
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const search = Route.useSearch()
  const params = Route.useParams()

  const { data: authData } = Auth.useAuthData()
  const zero = Zero.useZero()

  const [channel] = useQuery(
    queries.channelWithPermissions(authData, { id: context.channel.id }),
    { ttl: "5m" },
  )
  const channelId = React.useMemo(
    () => channel?.id ?? context.channelId,
    [channel?.id, context.channelId],
  )
  const repositoryId = React.useMemo(
    () => channel?.repository_id ?? context.repositoryId,
    [channel?.repository_id, context.repositoryId],
  )

  const [account] = useQuery(
    queries.accountWithMemberships(authData, {
      channelId,
      organizationId:
        channel?.repository?.organization_id ??
        (context.repository.owner.type === "organization"
          ? context.repository.owner.id
          : undefined),
      repositoryId,
    }),
    { enabled: Boolean(authData?.id) },
  )

  const [threadSubscription] = useQuery(
    queries.threadSubscription(authData, {
      threadId: search.threadId ?? "",
    }),
    { enabled: Boolean(search.threadId && authData?.id) },
  )

  const canSendMessage = useHasChannelPermission({
    account,
    channelId,
    channelPermissions: channel?.permissions ?? [],
    channelMember: account?.channel_memberships,
    organizationMember: account?.organization_memberships,
    repository: channel?.repository,
    repositoryMember: account?.repository_memberships,
    permissionId: "channel.send_message",
  })

  const repositoryOwner = React.useMemo(() => {
    const repository = channel?.repository
    if (!repository) return context.repository.owner
    if (repository.account)
      return { ...repository.account, type: "account" } as const
    if (repository.organization)
      return { ...repository.organization, type: "organization" } as const
    return context.repository.owner
  }, [context.repository.owner, channel?.repository])

  const channelSubscription = React.useMemo(
    () =>
      account?.channel_subscriptions.find(
        (subscription) => subscription.channel_id === channelId,
      ),
    [account?.channel_subscriptions, channelId],
  )

  const onSubmit = React.useCallback<MessageEditor.Props["onSubmit"]>(
    async (content, uploads) => {
      await zero.mutate.message.create({
        id: Nanoid.generate(),
        attachments: uploads.map((upload) => ({
          blurhash: upload.meta?.blurhash,
          id: Nanoid.generate(),
          key: upload.key,
          height: upload.meta?.height,
          name: upload.file.name,
          size: upload.file.size,
          type: upload.file.type,
          width: upload.meta?.width,
        })),
        channelId,
        content,
        now: Date.now(),
        subscriptionId: channelSubscription?.id ?? Nanoid.generate(),
      }).server
    },
    [channelId, channelSubscription, zero.mutate.message.create],
  )

  const toggleSubscribe = React.useCallback(async () => {
    if (channelSubscription?.subscribed_at)
      await zero.mutate.channel_subscription.unsubscribe({
        id: channelSubscription.id,
      }).server
    else
      await zero.mutate.channel_subscription.subscribe({
        id: channelSubscription?.id ?? Nanoid.generate(),
        channelId,
      }).server
  }, [
    channelId,
    channelSubscription,
    zero.mutate.channel_subscription.subscribe,
    zero.mutate.channel_subscription.unsubscribe,
  ])

  const status = useZeroOnline()

  const isChannelUnread = React.useMemo(() => {
    const lastMessageAt = channel?.repository?.channels.find(
      (c) => c.id === channelId,
    )?.messages?.inserted_at
    return Boolean(
      channelSubscription?.subscribed_at &&
        lastMessageAt &&
        lastMessageAt > channelSubscription.last_read_at,
    )
  }, [channel?.repository?.channels, channelId, channelSubscription])

  React.useEffect(() => {
    async function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (isChannelUnread && channelSubscription?.subscribed_at)
          await zero.mutate.channel_subscription.markRead({
            channelId,
            id: channelSubscription.id,
            now: Date.now(),
          }).server
      }
    }
    document.addEventListener("keydown", keydown, true)
    return () => {
      document.removeEventListener("keydown", keydown, true)
    }
  }, [
    channelId,
    channelSubscription,
    isChannelUnread,
    zero.mutate.channel_subscription.markRead,
  ])

  return (
    <>
      <Layout.Content hide={search.max}>
        <div className="flex h-12 items-center justify-between border-neutral-6 border-b px-3.5">
          <div className="flex items-center gap-3.5">
            <Link
              className="hidden text-muted hover:text-default max-md:block"
              params={params}
              search={(x) => ({ ...x, nav: x.nav ? undefined : true })}
              to="/$owner/$repo/~/$channel"
            >
              <OcticonSidebarExpand16 className="size-4 rotate-180" />
            </Link>

            {channel && (
              <div className="flex items-center gap-1 font-medium text-[14px]">
                {channel.private ? (
                  <OcticonLock16 className="mt-px size-4 text-muted" />
                ) : (
                  <OcticonHash16 className="mt-px size-4 text-muted" />
                )}
                {channel.name}
                {!status && (
                  <Spinner className="mt-[1px] ml-[2px] size-3 text-muted" />
                )}
              </div>
            )}
          </div>
          {account && (
            <div>
              <button
                className="text-muted"
                onClick={toggleSubscribe}
                type="button"
              >
                {channelSubscription?.subscribed_at ? (
                  <>
                    <span className="sr-only">Unsubscribe</span>
                    <OcticonBellFill16 className="-mb-[3px] size-4" />
                  </>
                ) : (
                  <>
                    <span className="sr-only">Subscribe</span>
                    <OcticonBell16 className="-mb-[3px] size-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <MessageList
          activeSubscriptionId={
            channelSubscription?.subscribed_at
              ? channelSubscription?.id
              : undefined
          }
          channelId={channelId}
          lastReadAt={
            channelSubscription?.subscribed_at
              ? channelSubscription?.last_read_at
              : undefined
          }
          repositoryId={repositoryId}
          repositoryOwner={repositoryOwner}
        />

        <MessageEditorSection
          canSendMessage={canSendMessage}
          channelId={channel?.id ?? context.channelId}
          onSubmit={onSubmit}
          placeholder={
            <div className="flex items-center gap-1">
              Message{" "}
              <div className="flex items-center gap-px">
                {channel?.private ? (
                  <OcticonLock16 className="mt-0.5 size-3 text-muted" />
                ) : (
                  <OcticonHash16 className="mt-0.5 size-3 text-muted" />
                )}
                {channel?.name}
              </div>
            </div>
          }
          repositoryId={channel?.repository_id ?? context.repositoryId}
          uploadText={
            <div className="flex items-center gap-1">
              Upload to{" "}
              <div className="flex items-center gap-px">
                {channel?.private ? (
                  <OcticonLock16 className="mt-0.5 size-8 text-muted" />
                ) : (
                  <OcticonHash16 className="mt-0.5 size-8 text-muted" />
                )}
                {channel?.name}
              </div>
            </div>
          }
        />
      </Layout.Content>

      <Layout.EndSidebar
        visibility={
          search.messageId ? (search.max ? "maximized" : "visible") : "hidden"
        }
      >
        {search.messageId && (
          <Thread
            canSendMessage={canSendMessage}
            channelId={channelId}
            maximize={search.max}
            messageId={search.messageId}
            repositoryId={repositoryId}
            repositoryOwner={repositoryOwner}
            subscription={threadSubscription}
            threadId={search.threadId}
          />
        )}
      </Layout.EndSidebar>
    </>
  )
}

function MessageEditorSection(props: {
  id?: string | undefined
  canSendMessage: boolean | null
  channelId: string
  onSubmit: MessageEditor.Props["onSubmit"]
  placeholder?: string | React.ReactNode | undefined
  repositoryId: string
  uploadText?: string | React.ReactNode | undefined
}) {
  const {
    canSendMessage,
    channelId,
    id,
    onSubmit,
    placeholder,
    repositoryId,
    uploadText,
  } = props

  const { data: authData } = Auth.useAuthData()

  return (
    <div className="relative isolate m-4 mt-0 rounded bg-transparent">
      {authData ? (
        <div className="rounded-md bg-default">
          {canSendMessage ? (
            <MessageEditor
              channelId={channelId}
              key={id}
              onSubmit={onSubmit}
              placeholder={placeholder}
              repositoryId={repositoryId}
              uploadText={uploadText}
            />
          ) : canSendMessage === false ? (
            <div className="relative rounded-md border border-neutral-6 p-3 dark:border-neutral-7">
              <span className="pointer-events-none inline-block select-none text-[14px] text-muted leading-[22px]">
                You do not have permission to send messages in this channel.
              </span>
            </div>
          ) : null}
        </div>
      ) : (
        <Button
          className="min-h-[48px]"
          height="full"
          render={
            <a href={Auth.url(location.href)} type="button">
              <OcticonMarkGithub16 />
              <div>Continue with GitHub</div>
            </a>
          }
          width="full"
        />
      )}
    </div>
  )
}

const MessageList = React.memo(MessageList_)
function MessageList_(props: {
  activeSubscriptionId: string | undefined
  channelId: string
  isThread?: boolean | undefined
  lastReadAt: number | undefined
  messageId?: string | undefined
  repositoryId: string
  repositoryOwner: Pick<Row.account, "id" | "login">
  threadId?: string | undefined
}) {
  const {
    activeSubscriptionId: subscriptionId,
    channelId,
    isThread,
    lastReadAt,
    messageId,
    repositoryId,
    repositoryOwner,
    threadId,
  } = props

  const { data: authData } = Auth.useAuthData()

  const zero = Zero.useZero()
  const [anchor, setAnchor] = React.useState<Anchor>({
    direction: "backward",
    index: 0,
    start: undefined,
  })
  const [messages, messagesResult] = useQuery(
    queries.messagesForChannel(authData, {
      channelId,
      direction: anchor.direction,
      limit: cursorSize,
      messageId,
      start: anchor.start,
      threadId,
    }),
  )

  const [maxIndex, setMaxIndex] = React.useState(cursorSize)
  const [minIndex, setMinIndex] = React.useState(0)
  const count = React.useMemo(() => maxIndex - minIndex, [maxIndex, minIndex])
  const hasReachedOldest = React.useRef(false)
  const hasReachedNewest = React.useRef(true)
  const isFetching = React.useRef(false)

  const parentRef = React.useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count,
    // estimateSize: React.useCallback(() => defaultItemHeight, []),
    estimateSize: React.useCallback(
      (index) => {
        const message = getMessageForVirtualIndex(index, anchor, messages)
        if (!message) return defaultItemHeight
        const prevRow = messages[toArrayIndex(index, anchor) + 1]
        const baseHeight = shouldJoinMessages(message, prevRow)
          ? 30
          : defaultItemHeight
        const reactionsHeight = message.reactions.length ? 28 : 0
        const threadHeight = "thread" in message ? 34 : 0
        const attachmentsHeight = message.attachments.length ? 200 : 0
        return (
          (message.content.length === 0 ? 0 : baseHeight) +
          reactionsHeight +
          threadHeight +
          attachmentsHeight
        )
      },
      [anchor, messages],
    ),
    getItemKey: React.useCallback(
      (index) => {
        const message = getMessageForVirtualIndex(index, anchor, messages)
        if (!message) return index
        return message.id
      },
      [anchor, messages],
    ),
    getScrollElement: React.useCallback(() => parentRef.current, []),
    getGroupIdForIndex: React.useCallback(
      (index) => {
        const message = getMessageForVirtualIndex(index, anchor, messages)
        if (!message) return undefined
        return new Date(message.inserted_at).toDateString()
      },
      [anchor, messages],
    ),
    getGroupHeaderHeight: React.useCallback(() => 36, []),
    overscan: 5,
    reverse: true,
  })

  // update maxIndex based on cursor size
  React.useEffect(() => {
    if (messagesResult.type !== "complete") return
    if (anchor.direction === "backward") {
      if (messages.length < cursorSize) {
        console.log("index.top", anchor)
        hasReachedOldest.current = true
        setMaxIndex(anchor.index + messages.length)
      } else {
        console.log("index.up", anchor)
        setMaxIndex((maxIndex) =>
          Math.max(maxIndex, anchor.index + messages.length),
        )
      }
      return
    }
    if (anchor.direction === "forward") {
      console.log("index.down", anchor)
      return
    }
  }, [anchor, messages, messagesResult.type])

  // update data windowing at scroll thresholds
  const virtualIndexes = virtualizer.getVirtualIndexes()
  React.useEffect(() => {
    if (messagesResult.type !== "complete") return
    if (isFetching.current) return

    const startVirtualIndex = virtualIndexes.at(0)
    const endVirtualIndex = virtualIndexes.at(-1)
    if (startVirtualIndex === undefined || endVirtualIndex === undefined) return

    const threshold = Math.ceil(messages.length / 10)
    const distanceFromStart =
      anchor.direction === "backward"
        ? startVirtualIndex - anchor.index
        : startVirtualIndex - (anchor.index - messages.length)
    const distanceFromEnd =
      anchor.direction === "backward"
        ? anchor.index + messages.length - endVirtualIndex
        : anchor.index - endVirtualIndex
    // console.log({
    //   distanceFromStart,
    //   distanceFromEnd,
    //   startVirtualIndex,
    //   endVirtualIndex,
    //   anchor,
    // })

    // check for newer messages
    if (!hasReachedNewest.current && distanceFromStart <= threshold) {
      hasReachedOldest.current = false
      const arrayIndex = toBoundArrayIndex(
        endVirtualIndex + threshold * 2,
        anchor,
        messages.length,
      )
      const index = toIndex(arrayIndex, anchor) - 1
      const message = messages[arrayIndex]
      if (!message) return
      const start = {
        id: message.id,
        inserted_at: message.inserted_at,
        updated_at: message.updated_at,
      }
      const query = queries.messagesForChannel(authData, {
        channelId,
        direction: "forward",
        limit: cursorSize,
        start,
      })
      isFetching.current = true
      zero.run(query, { type: "complete" }).then((results) => {
        if (results.length < cursorSize) {
          console.log("bottom")
          hasReachedNewest.current = true
          setMinIndex(0)
          setAnchor({ direction: "backward", index: 0, start: undefined })
        } else {
          console.log("down", index, start)
          setAnchor({ direction: "forward", index, start })
        }
        isFetching.current = false
      })
      return
    }

    // check for older messages
    if (!hasReachedOldest.current && distanceFromEnd <= threshold) {
      hasReachedNewest.current = false
      const arrayIndex = toBoundArrayIndex(
        startVirtualIndex - threshold * 2,
        anchor,
        messages.length,
      )
      const message = messages[arrayIndex]
      if (!message) return
      const index = toIndex(arrayIndex, anchor) + 1
      const start = {
        id: message.id,
        inserted_at: message.inserted_at,
        updated_at: message.updated_at,
      }
      const query = queries.messagesForChannel(authData, {
        channelId,
        direction: "backward",
        limit: cursorSize,
        start,
      })
      isFetching.current = true
      zero.run(query, { type: "complete" }).then((results) => {
        if (results.length < cursorSize) {
          console.log("top", index, start)
          hasReachedOldest.current = true
          setMaxIndex(index + results.length)
        } else {
          console.log("up", index, start)
          setMaxIndex((maxIndex) => Math.max(maxIndex, index + results.length))
        }
        setAnchor({ direction: "backward", index, start })
        isFetching.current = false
      })
    }
  }, [
    anchor,
    authData,
    channelId,
    messages,
    messagesResult.type,
    virtualIndexes,
    zero.run,
  ])

  const editingMessageId = App.useStore((state) => state.editingMessageId)
  const lastReadAtIndex = React.useMemo(() => {
    if (!lastReadAt) return -1
    for (let index = 0; index < messages.length; index++) {
      const nextMessage = messages.at(index + 1)
      if (!nextMessage) continue
      if (nextMessage.inserted_at > lastReadAt) continue
      const message = messages.at(index)
      if (!message) continue
      if (message.inserted_at > lastReadAt) return index
      return -1
    }
    return -1
  }, [lastReadAt, messages])

  return (
    <div
      className="scrollbar-neutral-6 dark:scrollbar-neutral-7 scrollbar-width-thin scrollbar-gutter-stable -mb-2 flex flex-1 flex-col-reverse overflow-y-auto pt-2 pb-4"
      ref={parentRef}
    >
      <div
        className="shrink-0"
        style={{ height: virtualizer.getStartSpacerSize() }}
      />
      {virtualizer.getVirtualGroups().map((group) => (
        <div
          className="relative flex flex-col-reverse pt-2"
          data-group={group.id}
          key={group.id}
        >
          {group.items.map((item) => {
            const message = getMessageForVirtualIndex(
              item.index,
              anchor,
              messages,
            )
            if (!message) return <div key={item.key} />
            const arrayIndex = toArrayIndex(item.index, anchor)
            const lastInGroup = item.index === group.items.at(-1)?.index
            return (
              <div
                data-index={item.index}
                key={item.key}
                ref={virtualizer.measureElement}
              >
                <MessageRow
                  channelId={channelId}
                  editing={message.id === editingMessageId}
                  isThread={isThread}
                  isUnread={arrayIndex === lastReadAtIndex}
                  message={message}
                  prevMessage={
                    lastInGroup ? undefined : messages.at(arrayIndex + 1)
                  }
                  repositoryId={repositoryId}
                  repositoryOwner={repositoryOwner}
                  subscriptionId={subscriptionId}
                  threadId={threadId}
                />
              </div>
            )
          })}

          <div
            // could use `style={{ height: group.headerHeight - 8 }}`, but easier to add tailwind classes since `group.headerHeight` is split
            // between top of container padding and height of this element
            className="sticky -top-0.5 z-50 flex h-7 w-full justify-center pt-1.5 pb-1.5"
          >
            <div className="rounded-xl bg-default px-2 text-center font-medium text-muted text-xs">
              {new Date(group.id.toString()).toLocaleDateString(undefined, {
                dateStyle: "medium",
              })}
            </div>
          </div>
          {lastReadAtIndex === group.items.at(-1)?.index ? (
            <div>
              <div className="absolute inset-x-0 top-5.5 h-px w-full bg-red-4/80" />
              <div className="absolute end-0 top-3.5 isolate bg-default px-2 text-center font-medium text-red-4 text-xs capitalize">
                new
              </div>
            </div>
          ) : (
            <div className="absolute inset-x-0 top-5.5 h-px w-full bg-neutral-4 dark:bg-neutral-5" />
          )}
        </div>
      ))}
      <div
        className="shrink-0"
        style={{ height: virtualizer.getEndSpacerSize() }}
      />
    </div>
  )
}

const cursorSize = 100
const defaultItemHeight = 54

type Anchor = {
  direction: "backward" | "forward"
  index: number
  start: Pick<Row.message, "id" | "inserted_at" | "updated_at"> | undefined
}

function toArrayIndex(index: number, anchor: Anchor) {
  return anchor.direction === "backward"
    ? index - anchor.index
    : anchor.index - index
}
function toBoundArrayIndex(index: number, anchor: Anchor, length: number) {
  return Math.min(length - 1, Math.max(0, toArrayIndex(index, anchor)))
}
function toIndex(arrayIndex: number, anchor: Anchor) {
  return anchor.direction === "backward"
    ? arrayIndex + anchor.index
    : anchor.index - arrayIndex
}
function getMessageForVirtualIndex(
  index: number,
  anchor: Anchor,
  messages: QueryResultType<typeof queries.messagesForChannel>,
) {
  const arrayIndex = toArrayIndex(index, anchor)
  if (arrayIndex < 0 || arrayIndex >= messages.length) return undefined
  return messages[arrayIndex]
}

function shouldJoinMessages(
  message: Row.message & {
    account: Row.account | undefined
  },
  prevMessage:
    | (Row.message & {
        account: Row.account | undefined
      })
    | undefined,
) {
  return (
    !prevMessage?.deleted_at &&
    message.account?.id === prevMessage?.account?.id &&
    message.inserted_at <=
      (prevMessage?.inserted_at ?? Date.now()) + 10 * 60 * 1000
  )
}

const MessageRow = React.memo(MessageRow_)
function MessageRow_(props: {
  channelId: string
  editing: boolean
  isThread?: boolean | undefined
  isUnread: boolean
  message: Row.message & {
    account: Row.account | undefined
    attachments?: readonly Row.attachment[] | undefined
    reactions?: readonly Row.reaction[] | undefined
    thread?: Row.thread | undefined
  }
  prevMessage: (Row.message & { account: Row.account | undefined }) | undefined
  repositoryId: string
  repositoryOwner: Pick<Row.account | Row.organization, "id" | "login">
  subscriptionId: string | undefined
  threadId: string | undefined
}) {
  const {
    channelId,
    editing,
    message,
    prevMessage,
    isThread,
    isUnread,
    repositoryId,
    repositoryOwner,
    subscriptionId,
    threadId,
  } = props

  const navigate = Route.useNavigate()
  const params = Route.useParams()

  const { data: authData } = Auth.useAuthData()
  const zero = Zero.useZero()
  const onEdit = React.useCallback<MessageEditor.Props["onSubmit"]>(
    async (content) => {
      try {
        App.store.setState((state) => ({ ...state, editingMessageId: null }))
        await zero.mutate.message.update({
          id: message.id,
          content,
        }).server
      } catch (error) {
        App.store.setState((state) => ({
          ...state,
          editingMessageId: message.id,
        }))
        throw error
      }
    },
    [message.id, zero.mutate.message.update],
  )

  const startEditing = React.useCallback(() => {
    App.store.setState((state) => ({ ...state, editingMessageId: message.id }))
  }, [message.id])
  const stopEditing = React.useCallback(() => {
    App.store.setState((state) => ({ ...state, editingMessageId: null }))
  }, [])

  const onDelete = React.useCallback(async () => {
    await zero.mutate.message.delete({
      id: message.id,
    }).server
  }, [message.id, zero.mutate.message.delete])

  const onReact = React.useCallback(
    async (value: string) => {
      const accountId = authData?.id
      if (!accountId) return
      const reaction = message.reactions?.find(
        (reaction) =>
          reaction.account_id === accountId && reaction.emoji === value,
      )
      if (reaction)
        await zero.mutate.reaction.delete({ id: reaction.id }).client
      else
        await zero.mutate.reaction.create({
          emoji: value,
          id: Nanoid.generate(),
          messageId: message.id,
        }).client
    },
    [
      authData?.id,
      message.id,
      message.reactions,
      zero.mutate.reaction.delete,
      zero.mutate.reaction.create,
    ],
  )

  const onMarkRead = React.useCallback(async () => {
    const sharedArgs = {
      channelId,
      now: Date.now(),
      id: subscriptionId ? subscriptionId : Nanoid.generate(),
    }
    if (threadId)
      await zero.mutate.thread_subscription.markRead({
        ...sharedArgs,
        threadId,
      }).server
    else await zero.mutate.channel_subscription.markRead(sharedArgs).server
  }, [
    channelId,
    subscriptionId,
    threadId,
    zero.mutate.channel_subscription.markRead,
    zero.mutate.thread_subscription.markRead,
  ])

  const onMarkUnread = React.useCallback(async () => {
    const sharedArgs = {
      channelId,
      now: message.inserted_at - 1_000,
      id: subscriptionId ? subscriptionId : Nanoid.generate(),
    }
    if (threadId)
      await zero.mutate.thread_subscription.markRead({
        ...sharedArgs,
        threadId,
      }).server
    else await zero.mutate.channel_subscription.markRead(sharedArgs).server
  }, [
    channelId,
    message.inserted_at,
    subscriptionId,
    threadId,
    zero.mutate.channel_subscription.markRead,
    zero.mutate.thread_subscription.markRead,
  ])

  const edited = message.inserted_at !== message.updated_at
  const sameAccount = shouldJoinMessages(message, prevMessage)
  const isOwner = authData?.id === message.account?.id

  return (
    <div
      {...(edited ? { "data-edited": true } : {})}
      {...(editing ? { "data-editing": true } : {})}
      {...(sameAccount ? { "data-join": true } : {})}
      className="group relative w-full px-4 py-1 hover:bg-neutral-3/25 focus:bg-neutral-8/10 data-editing:hover:bg-transparent dark:focus:bg-neutral-8/5 dark:hover:bg-neutral-2/80"
    >
      {isUnread && prevMessage && (
        <div className="absolute inset-x-0 -top-2 z-50 flex items-center justify-end">
          <div className="h-px w-full bg-red-4/80" />
          <div className="mb-0.5 px-2 text-center font-medium text-red-4 text-xs capitalize">
            new
          </div>
        </div>
      )}
      <Message
        account={message.account!}
        edited={edited}
        editing={editing}
        join={sameAccount}
        message={message}
        onCancelEdit={stopEditing}
        onEdit={onEdit}
        repositoryId={repositoryId}
        repositoryOwner={repositoryOwner}
      >
        {!message.deleted_at &&
          message.attachments &&
          message.attachments.length > 0 && (
            <Message.Attachments attachments={message.attachments} />
          )}
        {/* TODO: Effecting scroll performance */}
        {!message.deleted_at &&
          message.reactions &&
          message.reactions.length > 0 && (
            <Message.ReactionBar
              currentAccountId={authData?.id}
              onReact={onReact}
              reactions={message.reactions}
            />
          )}
        {message.thread && Boolean(message.thread.message_count) && (
          <div className="@container w-full">
            <Link
              className="block w-full @2xl:max-w-[50%]"
              params={params}
              search={(x) => ({
                ...x,
                messageId: message.id,
                threadId: message.thread?.id,
              })}
              to="/$owner/$repo/~/$channel"
            >
              <Message.ThreadBar thread={message.thread} />
            </Link>
          </div>
        )}
      </Message>

      {/* TODO: Effecting scroll performance */}
      {!editing && !(message.deleted_at && isThread) && (
        <div className="absolute end-0 top-0 z-50 hidden -translate-y-[50%] px-2 group-focus-within:block group-hover:block group-has-aria-expanded:block">
          <Base.Toolbar.Root className="flex w-fit rounded-lg border border-default bg-muted p-0.5">
            {authData && !message.deleted_at && (
              <ReactionPicker
                menuButton={
                  <Base.Toolbar.Button
                    aria-label="React"
                    className="flex size-7 items-center justify-center rounded-md bg-button-invisible-rest text-default/50 hover:bg-button-invisible-hover hover:text-default active:bg-button-invisible-active"
                  >
                    <GitchatReactionAdd className="size-4" />
                  </Base.Toolbar.Button>
                }
                onSelect={onReact}
              />
            )}

            {!isThread && (
              <Base.Toolbar.Button
                aria-label="Thread"
                className="flex size-7 items-center justify-center rounded-md bg-button-invisible-rest text-default/50 hover:bg-button-invisible-hover hover:text-default active:bg-button-invisible-active"
                onClick={() => {
                  navigate({
                    search: (x) => ({
                      ...x,
                      messageId: message.id,
                      threadId: message.thread?.id,
                    }),
                  })
                }}
              >
                <GitchatThread className="size-4" />
              </Base.Toolbar.Button>
            )}

            {authData &&
              !isOwner &&
              (isThread ? message.thread_id !== null : true) &&
              subscriptionId &&
              (isUnread ? (
                <Base.Toolbar.Button
                  aria-label="Mark Read"
                  className="flex size-7 items-center justify-center rounded-md bg-button-invisible-rest text-default/50 hover:bg-button-invisible-hover hover:text-default active:bg-button-invisible-active"
                  onClick={onMarkRead}
                >
                  <OcticonRead16 className="size-4" />
                </Base.Toolbar.Button>
              ) : (
                <Base.Toolbar.Button
                  aria-label="Mark Unread"
                  className="flex size-7 items-center justify-center rounded-md bg-button-invisible-rest text-default/50 hover:bg-button-invisible-hover hover:text-default active:bg-button-invisible-active"
                  onClick={onMarkUnread}
                >
                  <OcticonUnread16 className="size-4" />
                </Base.Toolbar.Button>
              ))}

            {!message.deleted_at && isOwner && (
              <Base.Toolbar.Button
                aria-label="Edit"
                className="flex size-7 items-center justify-center rounded-md bg-button-invisible-rest text-default/50 hover:bg-button-invisible-hover hover:text-default active:bg-button-invisible-active"
                onClick={startEditing}
              >
                <OcticonPencil16 className="size-4" />
              </Base.Toolbar.Button>
            )}

            {!message.deleted_at && isOwner && (
              <Base.Toolbar.Button
                aria-label="Delete"
                className="flex size-7 items-center justify-center rounded-md bg-button-invisible-rest text-red-4/80 hover:bg-button-invisible-hover hover:text-red-4 active:bg-button-invisible-active"
                onClick={onDelete}
              >
                <OcticonTrash16 className="size-4" />
              </Base.Toolbar.Button>
            )}
          </Base.Toolbar.Root>
        </div>
      )}
    </div>
  )
}

function Thread(props: {
  canSendMessage: boolean | null
  channelId: string
  maximize?: boolean | undefined
  messageId: string
  repositoryId: string
  repositoryOwner: Pick<Row.account, "id" | "login">
  subscription:
    | Pick<Row.thread_subscription, "id" | "last_read_at" | "subscribed_at">
    | undefined
  threadId?: string | undefined
}) {
  const {
    canSendMessage,
    channelId,
    maximize,
    messageId,
    repositoryId,
    repositoryOwner,
    subscription,
    threadId,
  } = props

  const params = Route.useParams()
  const router = useRouter()
  const navigate = Route.useNavigate()

  const { data: authData } = Auth.useAuthData()
  const zero = Zero.useZero()
  const onSubmit = React.useCallback<MessageEditor.Props["onSubmit"]>(
    async (content, uploads) => {
      try {
        const threadId = props.threadId ?? Nanoid.generate()
        navigate({ search: (search) => ({ ...search, threadId }) })
        const now = Date.now()
        await zero.mutate.thread.createMessage({
          id: Nanoid.generate(),
          attachments: uploads.map((upload) => ({
            blurhash: upload.meta?.blurhash,
            id: Nanoid.generate(),
            key: upload.key,
            height: upload.meta?.height,
            name: upload.file.name,
            size: upload.file.size,
            type: upload.file.type,
            width: upload.meta?.width,
          })),
          channelId,
          content,
          now,
          parentMessageId: messageId,
          subscriptionId: subscription?.id ?? Nanoid.generate(),
          threadId,
        }).server
      } catch (error) {
        router.history.back()
      }
    },
    [
      channelId,
      messageId,
      props.threadId,
      navigate,
      router,
      subscription,
      zero.mutate.thread.createMessage,
    ],
  )

  const toggleSubscribe = React.useCallback(async () => {
    if (subscription?.subscribed_at)
      await zero.mutate.thread_subscription.unsubscribe({
        id: subscription.id,
      }).server
    else if (threadId)
      await zero.mutate.thread_subscription.subscribe({
        id: subscription?.id ?? Nanoid.generate(),
        channelId,
        threadId,
      }).server
  }, [
    channelId,
    subscription,
    threadId,
    zero.mutate.thread_subscription.subscribe,
    zero.mutate.thread_subscription.unsubscribe,
  ])

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex h-12 items-center justify-between border-neutral-6 border-b px-3.5">
        {canSendMessage !== null && (
          <>
            <div className="flex items-center gap-1.5 font-medium text-[14px]">
              Thread
            </div>

            <div className="flex items-center gap-2">
              {authData && threadId && (
                <div>
                  <button
                    className="text-muted"
                    onClick={toggleSubscribe}
                    type="button"
                  >
                    {subscription?.subscribed_at ? (
                      <>
                        <span className="sr-only">Unsubscribe</span>
                        <OcticonBellFill16 className="-mb-[3px] size-4" />
                      </>
                    ) : (
                      <>
                        <span className="sr-only">Subscribe</span>
                        <OcticonBell16 className="-mb-[3px] size-4" />
                      </>
                    )}
                  </button>
                </div>
              )}
              <Link
                className="text-muted hover:text-default max-md:hidden"
                params={params}
                search={(x) => ({ ...x, max: x.max ? undefined : true })}
                to="/$owner/$repo/~/$channel"
              >
                {maximize ? (
                  <OcticonSidebarCollapse16 className="size-4" />
                ) : (
                  <OcticonSidebarExpand16 className="size-4" />
                )}
              </Link>
              <Link
                className="text-muted hover:text-default"
                params={params}
                search={(x) => ({
                  ...x,
                  max: undefined,
                  messageId: undefined,
                  threadId: undefined,
                })}
                to="/$owner/$repo/~/$channel"
              >
                <OcticonX16 className="size-5" />
              </Link>
            </div>
          </>
        )}
      </div>

      <MessageList
        activeSubscriptionId={
          subscription?.subscribed_at ? subscription?.id : undefined
        }
        channelId={channelId}
        isThread
        lastReadAt={
          subscription?.subscribed_at ? subscription?.last_read_at : undefined
        }
        messageId={messageId}
        repositoryId={repositoryId}
        repositoryOwner={repositoryOwner}
        threadId={threadId}
      />

      <MessageEditorSection
        canSendMessage={canSendMessage}
        channelId={channelId}
        id={messageId}
        onSubmit={onSubmit}
        placeholder="Reply…"
        repositoryId={repositoryId}
      />
    </div>
  )
}
